<template>
  <div class="form-editor">
    <form-component
      v-for="(field, index) in formJson[formRoot]"
      :key="index"
      :field="field"
      @setSeqAboveTarget="setSeq($event, field, true)"
      @setSeqBelowTarget="setSeq($event, field, false)"
      @deleteField="deleteField($event)"
      @openContextMenu="toggleContextMenu($event, field, true)"
      @moveUp="moveUpDown($event, true)"
      @moveDown="moveUpDown($event, false)"
    ></form-component>
    <pop-up-editor
      :root="formRoot"
      @deleteField="deleteField($event)"
      @addNewField="addNewField"
      @changeRoot="changeRoot($event)"
      @updateDependencyParent="updateDependencyParent"
      @checkSpecialComp="checkSpecialComp()"
    ></pop-up-editor>
    <ul id="ctxMenu">
      <li @click="openPopUp()">Edit Component</li>
      <li @click="addComponent(POSITION_ABOVE)">Add Component Above</li>
      <li @click="addComponent(POSITION_BELOW)">Add Component Below</li>
    </ul>
  </div>
</template>

<script>
import { Bus } from "../main.js";
import FormComponent from "../components/FormComponent.vue";
import PopUpEditor from "./PopUpEditor.vue";
import {
  COMPONENT_TYPE,
  AREA_COMP_TYPE,
  DEFAULT_NEW_FIELD,
  DEFAULT_NEW_AREA,
  ROOT_NODE,
} from "../mixins/constant-mixin";

const POSITION_ABOVE = "above";
const POSITION_BELOW = "below";

export default {
  name: "FormEditor",
  components: {
    "form-component": FormComponent,
    "pop-up-editor": PopUpEditor,
  },
  props: {
    json: {
      type: Object,
    },
    root: {
      type: String,
    },
  },
  data() {
    return {
      formJson: this.json,
      formRoot: this.root,
      ctxMenuTarget: {
        html: "",
        field: "",
        position: null,
      },
      POSITION_ABOVE: POSITION_ABOVE,
      POSITION_BELOW: POSITION_BELOW,
      largestSeq: 0,
      // identifier for the createNewField to generate a new largest seq
      NEW_SEQ: -2,
    };
  },
  created() {
    if (!this.formJson || !this.formJson[this.formRoot]) {
      this.formJson = { fields: [] };
    } else if (this.formJson[this.formRoot].length === 0) {
      this.largestSeq = 0;  
    } else {
      this.largestSeq = this.formJson[this.formRoot][this.formJson[this.formRoot].length - 1].layout.seq;
    }
  },
  methods: {
    addNewField: function (field, multiple, addByCtxMenu) {
      let seq;
      if (!multiple || multiple === 1) {
        seq = field.layout.seq;
        multiple = 1;
      } else {
        seq = field[0].layout.seq;
      }
      // check empty form
      if (this.formJson[this.formRoot].length === 0) {
        if (multiple === 1) {
          this.formJson[this.formRoot].push(field);
        } else {
          field.forEach(e => this.formJson[this.formRoot].push(e));  
        }
        this.largestSeq += multiple;
        return;
      } else if (seq <= this.largestSeq) {
        // adding at the middle of the list
        // modify the seq of the existing components
        this.formJson[this.formRoot].forEach((e) => {
          if (e.layout.seq >= seq) {
            e.layout.seq+=multiple;
          }
        });
      }

      let fieldAdded;
      // inserting new field and moving HTML element
      for (let i = 0; i < multiple; i++) {
        let newField;
        if (multiple === 1) {
          newField = field;
        } else {
          newField = field[i];
        }
        this.formJson[this.formRoot].push(newField);
        
        // move the new HTML element to the desired place
        // changing the formJson.fields array index will trigger the v-for key,
        // which will cause unwanted output
        //* if it is add by using the contextMenu(right click menu),
        //* use the target from the contextMenu
        if (seq <= this.largestSeq) {
          this.$nextTick(() => {
            const components = Array.from(this.$el.querySelectorAll(".container"));
            fieldAdded = components.find(component => component.dataset.seq == newField.layout.seq);
            this.$el.removeChild(fieldAdded);
            if (addByCtxMenu) {
              this.ctxMenuTarget.html
                .insertAdjacentElement((this.ctxMenuTarget.position === POSITION_BELOW) ? "afterend" : "beforebegin", fieldAdded);
            } else {
              // find the HTML element next to the new element and insert the new element
              components
                .find((item) => item.dataset.seq == seq + multiple)
                .insertAdjacentElement("beforebegin", fieldAdded);
            }
          });
        } else {
          this.$nextTick(() => {
            const components = this.$el.querySelectorAll(".container");
            fieldAdded = components[components.length - 1];
          })
        }
      }
      this.$nextTick(() => {
        const fieldRect = fieldAdded.getBoundingClientRect();
        const formRect = this.$el.getBoundingClientRect();
        if (fieldRect.top < formRect.top || fieldRect.bottom > formRect.bottom) {
          fieldAdded.scrollIntoView({block: "nearest"});
        }
      })
      this.largestSeq += multiple;
    },
    deleteField: function (seq) {
      // remove HTML element
      const targetElement = document.querySelector(
        `.container[data-seq="${seq}"]`
      );
      this.$el.removeChild(targetElement);
      this.largestSeq--;

      // seq of -1 will be removed when the user clicks <Save>
      //// the v-for directive is using the index as key,
      //// if the field is removed now, it will cause wrong render
      //// if use the seq as key,
      //// it will also cause wrong render when changing the seq and dragging
      const target = this.formJson[this.formRoot].find(
        (field) => field.layout.seq == seq
      );
      target.layout.seq = -1;

      // the deleted radio/checkboxes or dropdown might be the parent of some components
      // update the components that have dependency on this parent component
      if (target.metadata.type === COMPONENT_TYPE.OPTION_GROUP || target.metadata.type === COMPONENT_TYPE.SELECT) {
        this.updateDependencyParent(target.metadata.fieldName, null);
        Bus.$emit("checkDependency");
      }

      // check if the deleted item is special component
      // if yes then look for other existing special component
      //// it is same function with this.checkSpecialComp(), but just integrated into .map loop
      let checkSpecComp = false;
      if (
        target.metadata.type === COMPONENT_TYPE.PDICS || 
        AREA_COMP_TYPE[target.metadata.type]
      ) {
        checkSpecComp = true;
      }

      // change the seq of the other field
      this.formJson[this.formRoot] = this.formJson[this.formRoot].map((field) => {
        if (field.layout.seq > seq) {
          field.layout.seq -= 1;
        }

        // look for any other existing special component
        if (checkSpecComp && field.layout.seq != -1) { 
          if (
            field.metadata.type === COMPONENT_TYPE.PDICS || 
            AREA_COMP_TYPE[field.metadata.type]
          ) {
            checkSpecComp = false;
          }
        }
        return field;
      });

      // unset the type field filtering if all the special components are removed
      if (checkSpecComp) {
        Bus.$emit("limitCompType", null);
      }
    },
    getDependencyList: function () {
      return this.formJson[this.formRoot].filter(
        (field) =>
          field.metadata.type.toUpperCase() === COMPONENT_TYPE.OPTION_GROUP ||
          field.metadata.type.toUpperCase() === COMPONENT_TYPE.SELECT
      );
    },
    getDupFieldNameList: function (fieldName) {
      let list = this.formJson[this.formRoot]
        .map(field => {
          if (
            field.layout.seq != -1 &&
            !!field.metadata.fieldName && 
            field.metadata.fieldName.includes(fieldName)) {
            return field.metadata.fieldName;
          } else {
            return "";
          }
        })
        .filter(name => name !== "");
      return list;
    },
    // when the parent name changed or the parent is deleted, update the components that have dependency on the parent
    updateDependencyParent: function (parentOldName, parentNewName) {
      this.formJson[this.formRoot] = this.formJson[this.formRoot]
        .map(field => {
          if (
            field.metadata.dependency && 
            field.metadata.dependency.parent && 
            field.metadata.dependency.parent === parentOldName
          ) {
            if (parentNewName) {
              // parent changed name, update the dependency parent name
              field.metadata.dependency.parent = parentNewName;
            } else {
              // parent deleted, delete the dependency property
              this.$delete(field.metadata, "dependency");
            }
          }
          return field;
        })
    },
    //// FOR DRAG AND DROP ////
    // draggingSeq = seq of the dragging element, which is the target element emitting the ondrag event
    // targetField = field object of the target element, which is the target element emitting the ondrop event
    // movingAboveTarget = to seperate the situations between setSeqAboveTarget and setSeqBelowTarget
    setSeq: function (draggingSeq, targetField, movingAboveTarget) {
      draggingSeq = parseInt(draggingSeq);
      const targetSeq = parseInt(targetField.layout.seq);
      const draggingField = this.formJson[this.formRoot].find(
        (field) => field.layout.seq === draggingSeq
      );
      if (draggingSeq < targetSeq) {
        this.formJson[this.formRoot].forEach((field) => {
          if (field.layout.seq > draggingSeq && field.layout.seq < targetSeq) {
            field.layout.seq--;
          }
        });
        if (movingAboveTarget) {
          this.$set(draggingField.layout, "seq", targetSeq - 1);
          /* Example situation of movingAboveTarget & situation 1:
           ** field (ignore)
           ** field (dragging)(ignore) // original place
           ** field (move up)
           ** field (move up) // dragging to here
           ** field (target)(ignore)
           ** field (ignore)
           */
        } else {
          this.$set(targetField.layout, "seq", targetSeq - 1);
          this.$set(draggingField.layout, "seq", targetSeq);
          /* Example situation of movingAboveTarget & situation 2:
           ** field (ignore)
           ** field (target)(move down) // dragging to here
           ** field (move down)
           ** field (move down)
           ** field (dragging)(ignore) // original place
           ** field (ignore)
           */
        }
      } else {
        this.formJson[this.formRoot].forEach((field) => {
          if (field.layout.seq > targetSeq && field.layout.seq < draggingSeq) {
            field.layout.seq++;
          }
        });
        if (movingAboveTarget) {
          this.$set(targetField.layout, "seq", targetSeq + 1);
          this.$set(draggingField.layout, "seq", targetSeq);
          /* Example situation of movingBelowTarget & situation 1:
           ** field (ignore)
           ** field (dragging)(ignore) // original place
           ** field (move up)
           ** field (move up)
           ** field (target)(move up) // dragging to here
           ** field (ignore)
           */
        } else {
          this.$set(draggingField.layout, "seq", targetSeq + 1);
          /* Example situation of movingBelowTarget & situation 2:
           ** field (ignore)
           ** field (target)(ignore)
           ** field (move down) // dragging to here
           ** field (move down)
           ** field (dragging)(ignore) // original place
           ** field (ignore)
           */
        }
      }
      // edit the HTML element to make changes on the web
      const components = Array.from(document.querySelectorAll(".container"));
      const dragging = components.find((e) => e.dataset.seq == draggingSeq);
      const target = components.find((e) => e.dataset.seq == targetSeq);
      this.$el.removeChild(dragging);
      if (movingAboveTarget) {
        target.insertAdjacentElement("beforebegin", dragging);
      } else {
        target.insertAdjacentElement("afterend", dragging);
      }
      
      // to fix the inline element not back to in-line
      // this happen when a non-inline element moved into two inline elements and moved away
      if (target.classList.contains("inline")) {
        const prev = target.previousElementSibling;
        const next = target.nextElementSibling;
        if (next && next !== dragging) {
          this.$el.removeChild(target);
          next.insertAdjacentElement("beforebegin", target);
        } else if (prev && prev !== dragging) {
          this.$el.removeChild(target);
          prev.insertAdjacentElement("afterend", target);
        }
      }
    },
    moveUpDown: function (seq, movingUp) {
      if ((movingUp && seq <= 1) || (!movingUp && seq >= this.largestSeq)) {
        return;
      }
      const currField = Array.from(
        this.$el.querySelectorAll(".container")
        ).find(elem => elem.dataset.seq == seq)
      const targetSeq = movingUp? currField.previousElementSibling.dataset.seq: currField.nextElementSibling.dataset.seq;
      const targetField = this.formJson[this.formRoot].find(field => field.layout.seq == targetSeq);
      this.setSeq(seq, targetField, movingUp)
    },
    toggleContextMenu: function (event, field, toggleOn) {
      const ctxMenu = document.getElementById("ctxMenu");
      if (toggleOn) {
        this.ctxMenuTarget.html = event.currentTarget;
        this.ctxMenuTarget.field = field;
        event.preventDefault();
        ctxMenu.style.display = "block";
        ctxMenu.style.left = `${event.pageX}px`;
        ctxMenu.style.top = `${event.pageY}px`;
        document.addEventListener("click", this.toggleContextMenu);
      } else {
        ctxMenu.style.display = "";
        ctxMenu.style.left = "";
        ctxMenu.style.top = "";
        document.removeEventListener("click", this.toggleContextMenu);
      }
    },
    //// FOR CONTEXT MENU ////
    openPopUp: function () {
      Bus.$emit("openPopUp", this.ctxMenuTarget.field);
    },
    // position = above / below
    addComponent: function (position) {
      this.ctxMenuTarget.position = position;
      // seq of the new component
      let seq = this.ctxMenuTarget.field.layout.seq;
      if (position === POSITION_BELOW) {
        seq += 1;
      }
      // create and insert the new component
      const newField = this.createNewField(seq);

      Bus.$emit("addByCtxMenu", newField);
    },
    createNewField: function (seq) {
      let field;
      if (this.formRoot === ROOT_NODE.FIELDS) {
        field = structuredClone(DEFAULT_NEW_FIELD);
      } else if (this.formRoot === ROOT_NODE.AREAS) {
        field = structuredClone(DEFAULT_NEW_AREA);
      }

      if (seq === this.NEW_SEQ) {
        field.layout.seq = this.largestSeq + 1;
      } else {
        field.layout.seq = seq;
      }
      return field;
    },
    changeRoot: function (root) {
      const curr = this.formJson[this.formRoot];
      this.formRoot = root;
      this.formJson = {};
      this.$set(this.formJson, root, curr);
    },
    checkSpecialComp: function () {
      let noSpecComp = true;
      this.formJson[this.formRoot].some((field) => {
        if (field.metadata.type === COMPONENT_TYPE.PDICS) {
          this.changeRoot(ROOT_NODE.FIELDS);
          Bus.$emit("limitCompType", ROOT_NODE.FIELDS);
          noSpecComp = false;
          return true; // break some loop
        } else if (AREA_COMP_TYPE[field.metadata.type]) {
          this.changeRoot(ROOT_NODE.AREAS);
          Bus.$emit("limitCompType", ROOT_NODE.AREAS);
          noSpecComp = false;
          return true; // break some loop
        }
      });
      if (noSpecComp) {
        Bus.$emit("limitCompType", null);
      }
    },
  },
  mounted() {
    // check for specific fields/areas components
    this.checkSpecialComp();

    // emit from Home
    Bus.$on("saveForm", () => {
      if (this.largestSeq === 0) {
        this.$emit("saveForm", "");
        return;
      }
      const json = {};
      json[this.formRoot] = this.formJson[this.formRoot].filter(
        (field) => field.layout.seq != -1
      );
      this.$emit("saveForm", JSON.stringify(json));
    });

    // emit from Home
    Bus.$on("addComponent", () => {
      const field = this.createNewField(this.NEW_SEQ);

      Bus.$emit("openPopUp", field, true);
    });

    Bus.$on("openPopUp", () => {
      this.$nextTick(() =>
        Bus.$emit("getDependency", this.getDependencyList())
      );
    });

    // emit from Home
    Bus.$on("loadJSON", () => {
      // remove the existing unsaved changes (especially Text),
      // to prevent affecting the display of sampleJSON 
      this.formJson = {};
      this.$nextTick(() => {
        if (this.json) {
          this.formJson = this.json;
          this.formRoot = this.root;
          this.largestSeq = this.json[this.root].length;
          this.changeRoot(this.formRoot);
          this.$el.scrollTop = 0;
          this.checkSpecialComp();
        } else {
          // this.json is null
          this.largestSeq = 0;
          this.formJson[this.formRoot] = [];
        }
      });
    });

    // emit from metadata modal that has user-defined fieldName (InputTextModal, SelectModal...)
    Bus.$on("getDupFieldName", (fieldName) => {
      const list = this.getDupFieldNameList(fieldName);
      Bus.$emit("sendDupFieldName", list);
    });
  },
  beforeDestroy() {
    Bus.$off("saveForm");
    Bus.$off("addComponent");
    Bus.$off("openPopUp");
    Bus.$off("loadJSON");
    Bus.$off("getDupFieldName");
  },
};
</script>

<style scoped>
.form-editor {
  height: 70vh;
  overflow: auto;
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
  scroll-behavior: smooth;
}

#ctxMenu {
  display: none;
  position: absolute;
  z-index: 2;
  background: #fff;
  border: 1px solid #b1b1b1;
  border-radius: 5px;
  border-top-left-radius: 0;
  padding: 2px;
  margin: 0;
  list-style: none;
  list-style-type: none;
  font-size: 14px;
}

#ctxMenu li {
  padding: 5px 1rem;
  list-style: none;
  cursor: pointer;
}

#ctxMenu li:hover {
  background: #f1f1f1;
}
</style>